import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useFormik } from 'formik';
import { useGridApiRef } from '@mui/x-data-grid';
import * as yup from 'yup';
import dayjs from 'dayjs';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import {
  listProjectOrders,
  setProjectOrdersFilters,
} from '../../modules/projectModule/project-orders';
import ProjectOrdersList from '../../components/project_orders/ProjectOrdersList';
import { getNumberComma } from '../../utils/numberUtils';
import { ProjectOrdersListBox } from '../../components/project_orders/ProjectOrdersListBox';
import OrderViewModal from '../../components/project_orders/OrderViewModal';
import ShareLinkBox from '../../components/common/ShareLinkBox';

const ProjectOrdersListContainer = () => {
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const { enqueueSnackbar } = useSnackbar();

  const { orderLetters, defaultFilters, error, loading } = useSelector(
    ({ projectModule, loading }) => ({
      orderLetters: projectModule.projectOrders.orderLetters,
      defaultFilters: projectModule.projectOrders.defaultFilters,
      error: projectModule.projectOrders.error,
      loading: loading['project/LIST_PROJECT_ORDERS'],
    }),
  );

  const [tableData, setTableData] = useState([]);

  const makeFilters = (filters) => {
    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : '';

    let searchData = {
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
    };

    return searchData;
  };

  useEffect(() => {
    dispatch(listProjectOrders(makeFilters(defaultFilters)));
  }, [dispatch, defaultFilters]);

  useEffect(() => {
    if (orderLetters) {
      setTableData(orderLetters);
    }
  }, [orderLetters]);

  const handleFilters = useCallback((name, value) => {
    const newFilters = { ...defaultFilters, [name]: value };

    dispatch(setProjectOrdersFilters(newFilters));
  }, []);

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        hide: true,
        type: 'string',
        editable: false,
      },
      {
        field: 'roUuid',
        hide: true,
        type: 'string',
        editable: false,
      },
      {
        field: 'roNo',
        headerName: '수주품의서번호',
        minWidth: 190,
        flex: 0.7,
        type: 'string',
        editable: false,
        // 번호 옆에 공유 주소 복사를 둔다. 수주는 따로 열리는 화면이 없어서
        // 주소는 소속 기회를 가리킨다 - 번호만 알면 바로 열린다.
        renderCell: (params) => (params?.row?.roNo ? (
          <ShareLinkBox
            compact
            text={params.row.roNo}
            path={`/go/${params.row.roNo}`}
          />
        ) : null),
      },
      // {
      //   field: 'expDay',
      //   headerName: '계약일',
      //   minWidth: 80,
      //   flex: 0.5,
      //   type: 'string',
      //   editable: false,
      // },
      // {
      //   field: 'siDt',
      //   headerName: '계약 종료일',
      //   minWidth: 80,
      //   flex: 0.5,
      //   type: 'string',
      //   editable: false,
      // },

       {
        field: 'crtDptNm',
        headerName: '부서명',
        minWidth: 80,
        flex: 0.5,
        type: 'string',
        editable: false,
      },

      {
        field: 'crtNm',
        headerName: '담당자',
        minWidth: 60,
        flex: 0.5,
        type: 'string',
        editable: false,
      },
      {
        field: 'partnerNm',
        headerName: '매출처명',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
      },
      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 300,
        flex: 1,
        description: '',
        align: 'left',
        headerAlign: 'left',
        editable: false,
        renderCell: (params) => (
          <>
            {params.value}
            <IconButton
              aria-label="preview"
              color="success"
              size="small"
              onClick={() => handleOpen(params.row)}
            >
              <PreviewIcon />
            </IconButton>
          </>
        ),
      },

      // {
      //   field: 'crtNm',
      //   headerName: '등록자',
      //   minWidth: 80,
      //   flex: 1,
      //   type: 'string',
      //   editable: false,
      // },

      {
        field: 'roTotalAmt',
        headerName: '매출액',
        minWidth: 80,
        flex: 1,
        type: 'number',
        align: 'right',
        editable: false,
        valueFormatter: (params) => {
          //console.log(params);
          if (!params || params.value === undefined) return '0';
          return getNumberComma(params.value || 0);
        },
        renderCell: (params) => 
          {
            //console.log(params);
          if (!params || params.value === undefined) return '0';
          return getNumberComma(params.value || 0);
        },

      },
      {
        field: 'expBuy',
        headerName: '매입액',
        minWidth: 80,
        flex: 1,
        type: 'number',
        align: 'right',
        editable: false,
        valueFormatter: (params) => {
          if (!params || params.value === undefined) return '0';
          return getNumberComma(params.value || 0);
        },
        renderCell: (params) => {
          if (!params || params.value === undefined) return '0';
          return getNumberComma(params.value || 0);
        },
      },
      {
        field: 'roProfitAmt',
        headerName: '매출이익',
        minWidth: 80,
        flex: 1,
        type: 'number',
        align: 'right',
        editable: false,
        valueFormatter: (params) => {
          if (!params || params.value === undefined) return '0';
          return getNumberComma(params.value || 0);
        },
        renderCell: (params) => {
          if (!params || params.value === undefined) return '0';
          return getNumberComma(params.value || 0);
        },
      },
      
      {
        field: 'ordYn',
        headerName: '사용여부',
        minWidth: 30,
        flex: 1,
        type: 'string',
        editable: false,
      },
      {
        field: 'crtDt',
        headerName: '등록일',
        minWidth: 100,
        flex: 1,
        type: 'date',
        valueGetter: (params) => {
          if (!params) return null;
          try {
            return new Date(params);
          } catch (e) {
            console.error('Invalid date format:', params);
            return null;
          }
        },
        valueFormatter: (params) => {
          if (!params) return '';
          return dayjs(params).format('YYYY-MM-DD');
        },
        editable: false,
      },
    ],
    [],
  );

  const initialValues = {
    searchKeyword: defaultFilters.searchKeyword,
    searchWords: defaultFilters.searchWords,
    searchStrtDt: defaultFilters.searchStrtDt,
    searchEndDt: defaultFilters.searchEndDt,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('검색 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        let searchData = {
          searchKeyword: values.searchKeyword,
          searchWords: values.searchWords,
          searchStrtDt: values.searchStrtDt,
          searchEndDt: values.searchEndDt,
        };
        dispatch(setProjectOrdersFilters(searchData));

        setSubmitting(false);
      }, 1000);
    },
  });

  const [showOrder, setShowOrder] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const handleClose = () => {
    setShowOrder(false);
    setSelectedOrder(null);
  };

  const handleOpen = (param) => {
    setSelectedOrder(param);
    setShowOrder(true);
  };

  return (
    <>
      <BreadcrumbsContainer
        title="영업/수주품의서 관리/수주품의서 리스트"
        icon="order"
      />
      <ProjectOrdersListBox formik={formik} />
      <ProjectOrdersList
        apiRef={apiRef}
        columns={columns}
        rows={tableData}
        error={error}
        loading={loading}
        columnVisibilityModel={{
          id: false,
          roUuid: false
        }} 
      />
      {showOrder && (
        <OrderViewModal
          id={selectedOrder?.roUuid}
          open={showOrder}
          handleClose={handleClose}
          hiddenFields={{
            id: true,
            roUuid: true
          }}
        />
      )}
    </>
  );
};

export default ProjectOrdersListContainer;
